/**
 * Properties of a FeedComponent (Komponente / Zutat).
 *
 * @note The interfaces in this file are more or less copied from the supabase generated types in the users-svc.
 */

import { boolean, number, object, string, type InferType } from "yup"

const FeedComponentIdSchema = object({
  id: string().required(),
})

/**
 * Feed component category. Mostly the ATLAS taxonomy
 * (@see ATLAS/RationFeedstuffRequest/type in [1]), plus `leftovers` which has no
 * ATLAS equivalent. Values not yet offered in the UI dropdown are commented.
 *
 * [1] https://github.com/atlasH2020-templates/feeding_data/blob/v0/open-api.yaml
 */
export const FeedComponentGroups = [
  "miscellaneous", // Sonstiges (default)
  "grass_silage", // Gras
  "corn_silage", // Mais
  "leftovers", // Restfutter -- not an ATLAS value
  "premix", // Vormischung
  "water", // Wasser
  "mineral", // Mineralfutter
  "concentrate", // Kraftfutter
  "dry_forage", // ATLAS value, not yet offered in the UI dropdown
  "alfa_silage", // ATLAS value, not yet offered in the UI dropdown
  "other_wet_forage", // ATLAS value, not yet offered in the UI dropdown
  "grain", // ATLAS value, not yet offered in the UI dropdown
  "fatty_acid_source", // ATLAS value, not yet offered in the UI dropdown
  "triglycerides", // ATLAS value, not yet offered in the UI dropdown
  "cp_source", // ATLAS value, not yet offered in the UI dropdown
  "rup_source", // ATLAS value, not yet offered in the UI dropdown
  "vitamin", // ATLAS value, not yet offered in the UI dropdown
  "forage", // ATLAS value, not yet offered in the UI dropdown
  "commercial", // ATLAS value, not yet offered in the UI dropdown
] as const
export type FeedComponentGroup = (typeof FeedComponentGroups)[number]
export const FeedComponentDefaultGroup: FeedComponentGroup = "miscellaneous"

/**
 * Water has 0% dry matter by definition. Components in this group have their
 * `ts_gehalt_percent` forced to 0 on both save and load.
 */
export const isWaterGroup = (group: string | null | undefined): boolean =>
  group === "water"

export const FeedComponentSchemaNameMaxLength = 20
export const FeedComponentSchema = object({
  created_at: string().required(),
  created_by: string().required(),
  group: string()
    .nullable()
    .oneOf([...FeedComponentGroups, null])
    .default(FeedComponentDefaultGroup),
  id: string().required(),
  image: string().nullable(),
  include_in_stats: boolean().required(),
  name: string().required().max(FeedComponentSchemaNameMaxLength),
  org_id: string().required(),
  // is_dry_mass is derived server-side from ts_gehalt_percent (> 0), not user-settable.
  is_dry_mass: boolean().optional(),
  price_per_kg: number().nullable().positive(),
  ts_gehalt_percent: number()
    // .transform(x => isNaN(x) ? 100 : x)
    .nullable()
    .min(0)
    .max(100)
    .default(0),
  gegenschneiden_ausfahren: boolean(),
  elevatorband_geschwindigkeit: number()
    .transform((x) => {
      // return undefined, assuming it will be replaced by the .default value
      if (null === x || undefined === x) {
        return undefined
      }
      return isNaN(x) ? undefined : x
    })
    .default(0)
    .positive()
    .min(0)
    .max(100),
  updated_at: string().defined(),
  updated_by: string().defined(),
  deleted_at: string().nullable(),
  deleted_by: string().nullable(),
})

/**
 * Create-DTO: All data that's transferred from FE to BE for a create request.
 */
export const FeedComponentCreateSchema = FeedComponentSchema.omit([
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "deleted_at",
  "deleted_by",
]).concat(
  object({
    id: string().optional(),
  })
)

/**
 * Update-DTO: All data that's transferred from FE to BE for a update request.
 *
 * @note Of course, `id` is required for an update. It is taken from the path.
 * @note `id` and `org_id` cannot be changed in the FE, so there's no point including them in the request.
 */
export const FeedComponentUpdateSchema = FeedComponentCreateSchema.omit([
  "org_id",
])

/**
 * Delete-DTO: All data that's transferred from FE to BE for a delete request.
 */
export const FeedComponentDeleteSchema = FeedComponentIdSchema.clone()

export type FeedComponentDto = InferType<typeof FeedComponentSchema>
export type FeedComponentCreateDto = InferType<typeof FeedComponentCreateSchema>
export type FeedComponentUpdateDto = InferType<typeof FeedComponentUpdateSchema>
export type FeedComponentDeleteDto = InferType<typeof FeedComponentDeleteSchema>

// type Prettify<T> = {
//   [K in keyof T]: T[K]
// } & {}
// type fcDto = Prettify<FeedComponentDto>
// type fcCDto = Prettify<FeedComponentCreateDto>
// type fcUDto = Prettify<FeedComponentUpdateDto>
// type fcDDto = Prettify<FeedComponentDeleteDto>
